import { cloneElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  DropdownSelect,
  RadioButtonGroup,
  RangeSlider,
  Spinner,
  Typography,
} from "@/components/atoms";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { transferRateKind } from "@/redux/slices/clientBooking/bookingPayload";
import {
  setBookedForDuration,
  setBookingExtras,
  setBookingTransferType,
  setBookingVehicle,
  setFixedPrice,
  setJobRate,
  setNumOfBags,
  setNumOfPassengers,
} from "@/redux/slices/clientBooking/clientBookingSlice";
import { ClientBookingExtra } from "@/redux/slices/clientBooking/types";
import {
  selectBookedForDuration,
  selectBookingExtras,
  selectBookingTransferType,
  selectBookingVehicle,
  selectEditJobId,
  selectNumOfBags,
  selectNumOfPassengers,
} from "@/redux/slices/clientBooking/selectors";
import { clsx, formatCurrency } from "@/utils";
import "./VehicleTransferPanel.css";
import { useBookingViewOnly } from "./BookingViewOnlyContext";
import { ViewOnlyFieldset } from "./ViewOnlyFieldset";
import { useBookingData } from "./hooks/useBookingData";
import { useForceValidate } from "./useForceValidate";
import { AddExtrasModal } from "./AddExtrasModal";
import { extrasLabel, filterVehiclesByTransfer } from "./vehicleTransfer";

type Layout = "card" | "sheet";

// Required selections — passengers/bags always carry a valid default and extras are optional. Transfer
// type has no default (user must choose); the vehicle auto-defaults to the first available, so it's
// only flagged when nothing is selectable. See validate() below.
type FieldErrors = Partial<Record<"transferTypeId" | "vehicleId" | "duration", string>>;

interface VehicleTransferPanelProps {
  /** "card" = desktop side panel; "sheet" = mobile bottom sheet (adds a Close action). */
  layout: Layout;
  onClose: () => void;
  /**
   * "card" layout only: called after Proceed commits the selection so the wizard opens the next Trip
   * Details sub-step (Other Details). The "sheet" layout closes back to the list instead.
   */
  onAdvance?: () => void;
  /** When true, show the required-field errors immediately (the user was routed here by a failed exit). */
  forceValidate?: boolean;
}

/** A form section: label above its control. */
const Field = ({ label, children }: React.PropsWithChildren<{ label: string }>) => (
  <div className="flex flex-col gap-2">
    <Typography variant="action" className="block text-neutral-dark-gray">
      {label}
    </Typography>
    {children}
  </div>
);

/* --- Passenger scale ---------------------------------------------------------------------------
   The row of ticks and numbers under the passenger slider. Its density has to hold from a 1-seat
   car to a coach: passengers is validated server-side as an integer > 0 with no ceiling, so the
   scale can't assume a stop per seat is readable. */

/** Room a numbered stop needs to earn its number. Comfortably wider than the point where numbers
 *  would actually touch (~34px): the scale is a sense of range, not a ruler, so an 11-seater reads
 *  1·3·5·7·9·11 rather than every seat. Small capacities still number every stop — at 8 seats the
 *  gaps are 64px, so there is nothing to thin out. */
const STOP_LABEL_MIN_GAP = 56;
/** Room between tick marks; any closer and the scale reads as one solid comb. */
const STOP_TICK_MIN_GAP = 6;
/** Track width assumed before the first measurement (and in jsdom, which never lays out). */
const TRACK_WIDTH_FALLBACK = 400;

/**
 * Interval between numbered stops: the smallest divisor of the range that keeps numbers at least
 * STOP_LABEL_MIN_GAP apart. Using a divisor makes the last number land exactly on `max` with every
 * gap identical (max 16 -> 1·4·7·10·13·16). A fixed "every other" step instead strands the final
 * two numbers on neighbouring ticks whenever `max` is even (1·3·…·15·16).
 *
 * Only a divisor CLOSE to the minimum will do, hence the search limit: `max - 1` always divides
 * itself, so an unlimited search would answer a prime range (max 20, max 48) with the whole span
 * and number nothing but the two ends. Those ranges fall back to the minimum and accept one uneven
 * gap at the end, where passengerLabelledStops drops the number that would crowd `max`.
 */
const passengerLabelStep = (max: number, trackPx: number) => {
  if (max <= 1) return 1;
  const gap = trackPx / (max - 1);
  const min = Math.max(1, Math.ceil(STOP_LABEL_MIN_GAP / gap));
  const limit = Math.min(Math.max(min * 2, min + 1), max - 1);
  for (let step = min; step <= limit; step += 1) {
    if ((max - 1) % step === 0) return step;
  }
  return min;
};

/** The stops carrying a number: both ends, plus every step between them that still clears `max`. */
const passengerLabelledStops = (max: number, trackPx: number) => {
  const stops = new Set([1, max]);
  const gap = max > 1 ? trackPx / (max - 1) : trackPx;
  const step = passengerLabelStep(max, trackPx);
  for (let n = 1 + step; n < max; n += step) {
    if ((max - n) * gap >= STOP_LABEL_MIN_GAP) stops.add(n);
  }
  return stops;
};

/** Draw only every Nth tick, so a coach-sized capacity still reads as a scale. */
const passengerTickStep = (max: number, trackPx: number) => {
  if (max <= 1) return 1;
  return Math.max(1, Math.ceil(STOP_TICK_MIN_GAP / (trackPx / (max - 1))));
};

/**
 * "Vehicle and Transfer" — the Vehicle sub-panel of the Trip Details step. Reads the bookable
 * transfer types / vehicles / extras from GET /v1/cust/data. Selecting a transfer type filters the
 * vehicle list by its `vehicle_exclusions` (legacy-sheet parity). Passenger + bag counts range over
 * the fleet's largest capacities so the capacity warning is reachable when the chosen vehicle is too
 * small. The selection is committed to clientBookingSlice on Proceed (not live), matching Figma.
 */
export const VehicleTransferPanel = ({ layout, onClose, onAdvance, forceValidate = false }: VehicleTransferPanelProps) => {
  const dispatch = useAppDispatch();
  const viewOnly = useBookingViewOnly();
  // Edit mode relabels the commit CTA "Confirm" (it closes the sub-panel rather than advancing).
  const isEdit = useAppSelector(selectEditJobId) != null;
  // Scope-aware: authed client data (/v1/cust/data) or guest job-data, normalised to one shape.
  const { data, isLoading } = useBookingData();

  const committedVehicle = useAppSelector(selectBookingVehicle);
  const committedTransfer = useAppSelector(selectBookingTransferType);
  const committedPassengers = useAppSelector(selectNumOfPassengers);
  const committedBags = useAppSelector(selectNumOfBags);
  const committedExtras = useAppSelector(selectBookingExtras);
  const committedDuration = useAppSelector(selectBookedForDuration);

  // Local draft, seeded from the committed selection; committed on Proceed.
  const [transferTypeId, setTransferTypeId] = useState<string | undefined>(committedTransfer ? String(committedTransfer.id) : undefined);
  const [vehicleId, setVehicleId] = useState<string | undefined>(committedVehicle ? String(committedVehicle.vehicle_id) : undefined);
  const [selectedExtras, setSelectedExtras] = useState<ClientBookingExtra[]>(committedExtras ?? []);
  const [extrasOpen, setExtrasOpen] = useState(false);
  const [passengers, setPassengers] = useState(committedPassengers ?? 1);
  const [bags, setBags] = useState(committedBags ?? 0);
  // Duration in minutes, only used by hourly/daily transfer types.
  const [duration, setDuration] = useState<number | undefined>(committedDuration || undefined);

  const transferTypes = useMemo(() => {
    const listed = data?.transfer_types ?? [];
    // A replayed booking's own type when the list no longer offers it — deactivated, deleted or admin-only
    // (hydrated from the detail by mapBookingDetailToViewDraft). Listed so the dropdown SHOWS the type the
    // booking was made on and Confirm keeps it; choosing another entry is the only thing that changes it.
    // Replay only: a fresh draft never carries a type the list did not offer.
    const keepCommitted = (isEdit || viewOnly) && committedTransfer && !listed.some((t) => String(t.id) === String(committedTransfer.id));
    if (!keepCommitted) return listed;
    return [
      {
        id: committedTransfer.id,
        label: committedTransfer.label ?? "",
        type: committedTransfer.type ?? "",
        special_event: committedTransfer.special_event ?? false,
        vehicle_exclusions: committedTransfer.vehicle_exclusions ?? [],
      },
      ...listed,
    ];
  }, [data, isEdit, viewOnly, committedTransfer]);
  const vehicles = useMemo(() => data?.vehicles ?? [], [data]);
  const extras = data?.extras ?? [];

  // `/v1/cust/data` can return ids as numbers; normalise to string everywhere so the DropdownSelect
  // (and our lookups) compare like-for-like.
  const selectedTransfer = useMemo(() => transferTypes.find((t) => String(t.id) === transferTypeId), [transferTypes, transferTypeId]);

  // The chosen transfer type's billing shape. Hourly/daily require a duration; journey doesn't.
  const rateKind = transferRateKind(selectedTransfer?.type);
  const durationOptions = useMemo(
    () => (rateKind === "hourly" ? data?.hourly_rates ?? [] : rateKind === "daily" ? data?.daily_rates ?? [] : []),
    [rateKind, data]
  );

  // Keep the duration valid for the chosen rate: clear it for journey types; for hourly/daily default
  // to the first preset (mobile parity) and drop a value the new option set no longer offers.
  useEffect(() => {
    if (rateKind === "journey") {
      setDuration(undefined);
      return;
    }
    setDuration((current) => {
      if (current && durationOptions.some((o) => o.value === current)) return current;
      return durationOptions[0]?.value;
    });
  }, [rateKind, durationOptions]);
  const availableVehicles = useMemo(() => filterVehiclesByTransfer(vehicles, selectedTransfer), [vehicles, selectedTransfer]);
  const selectedVehicle = useMemo(() => availableVehicles.find((v) => String(v.vehicle_id) === vehicleId), [availableVehicles, vehicleId]);

  // The count ranges come from the selected vehicle (mirrors the old sheet, which builds passengers
  // from the vehicle's seat count and rebuilds on vehicle change).
  // The count ranges come from the selected vehicle (mirrors the old sheet, which builds passengers
  // from the vehicle's seat count and rebuilds on vehicle change).
  const maxPassengers = selectedVehicle?.max_passengers ?? 1;
  const maxBags = selectedVehicle?.max_bags ?? 0;

  // How many numbers fit depends on how wide the track actually is, and that varies by layout: the
  // sheet sub-panel is 496px on desktop but full-width on mobile, and the card layout differs again.
  // So measure the scale row — it is exactly as wide as the rail, and its stops are absolutely
  // positioned, so reading its width can't feed back into layout. A callback ref (rather than an
  // effect) because the row only mounts once the vehicle data has loaded.
  const [trackWidth, setTrackWidth] = useState(TRACK_WIDTH_FALLBACK);
  const scaleObserver = useRef<ResizeObserver | null>(null);
  const measureScale = useCallback((node: HTMLDivElement | null) => {
    scaleObserver.current?.disconnect();
    scaleObserver.current = null;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      if (width > 0) setTrackWidth(width);
    });
    observer.observe(node);
    scaleObserver.current = observer;
  }, []);
  useEffect(() => () => scaleObserver.current?.disconnect(), []);

  const labelledStops = useMemo(() => passengerLabelledStops(maxPassengers, trackWidth), [maxPassengers, trackWidth]);
  const tickStep = useMemo(() => passengerTickStep(maxPassengers, trackWidth), [maxPassengers, trackWidth]);
  // A closed booking greys the scale along with the rail and handle, so the slider reads as disabled
  // next to the other greyed-out fields instead of as a live control.
  const tickTone = viewOnly
    ? { live: "bg-neutral-mid-gray", numbered: "bg-neutral-mid-gray", between: "bg-neutral-gray" }
    : { live: "bg-primary", numbered: "bg-neutral-black", between: "bg-neutral-mid-gray" };
  const numberTone = viewOnly
    ? { live: "text-neutral-silver-gray", fixed: "text-neutral-silver-gray" }
    : { live: "text-primary", fixed: "text-neutral-black" };

  // No default transfer type — the client must choose one (it's a required field).

  // Keep the vehicle valid for the chosen transfer type: default to the first available, and drop a
  // selection the transfer type now excludes.
  useEffect(() => {
    if (!data) return;
    const stillValid = vehicleId && availableVehicles.some((v) => String(v.vehicle_id) === vehicleId);
    if (!stillValid) setVehicleId(availableVehicles.length ? String(availableVehicles[0].vehicle_id) : undefined);
  }, [data, availableVehicles, vehicleId]);

  // Clamp the counts into the selected vehicle's capacity whenever the vehicle changes — the old
  // sheet caps a too-high passenger count down to the new vehicle's seats.
  useEffect(() => {
    if (!selectedVehicle) return;
    setPassengers((p) => Math.min(Math.max(p, 1), selectedVehicle.max_passengers));
    setBags((b) => Math.min(Math.max(b, 0), selectedVehicle.max_bags));
  }, [selectedVehicle]);

  const [errors, setErrors] = useState<FieldErrors>({});

  // The chosen extras, named and priced. View-only reads the hydrated selection straight off the
  // draft so it still shows an extra the operator has since retired; editing reads the same list,
  // which the modal replaces wholesale on confirm.
  const chosenExtras = viewOnly ? committedExtras ?? [] : selectedExtras;
  const extrasCount = chosenExtras.reduce((total, extra) => total + (extra.quantity ?? 1), 0);
  const extrasTotal = chosenExtras.reduce((sum, extra) => sum + (extra.amount ?? 0) * (extra.quantity ?? 1), 0);

  // Validate the required selections and surface a per-field error for anything missing. Returns
  // whether the selection is complete, so both Proceed and forceValidate can share it.
  const validate = (): boolean => {
    // Transfer type is genuinely user-required — it has no default.
    const transferMissing = !transferTypeId;
    // Vehicle auto-defaults to the first available (see the effect above), so it's only really
    // missing when the loaded data + chosen transfer leave nothing selectable. Gating on `data` +
    // `availableVehicles` (not the raw `vehicleId`) also avoids a false "Please select a vehicle"
    // during load, or on the render before the default effect has applied.
    const vehicleMissing = !vehicleId && !!data && availableVehicles.length === 0;
    // Hourly/daily transfer types can't be quoted/booked without a duration.
    const durationMissing = rateKind !== "journey" && !duration;
    setErrors({
      transferTypeId: transferMissing ? "Please select a transfer type" : undefined,
      vehicleId: vehicleMissing ? "Please select a vehicle" : undefined,
      duration: durationMissing ? "Please select a duration" : undefined,
    });
    return !transferMissing && !vehicleMissing && !durationMissing;
  };
  // Routed here by a failed "Next Step": show the missing-selection errors right away.
  useForceValidate(forceValidate, validate);

  const handleProceed = () => {
    if (!validate()) return;
    if (!selectedVehicle || !selectedTransfer) return;
    dispatch(
      setBookingVehicle({
        vehicle_id: selectedVehicle.vehicle_id,
        uuid: selectedVehicle.uuid,
        name: selectedVehicle.name,
        max_passengers: selectedVehicle.max_passengers,
        max_bags: selectedVehicle.max_bags,
      })
    );
    dispatch(setBookingTransferType({ id: Number(selectedTransfer.id), label: selectedTransfer.label, type: selectedTransfer.type }));
    // Rate + duration follow the transfer type (mobile: job_rate = transfer_type.type). Journey types
    // carry no duration; hourly/daily send the chosen minutes.
    dispatch(setJobRate(selectedTransfer.type));
    dispatch(setBookedForDuration(rateKind === "journey" ? 0 : duration));
    // The price override (set in the Price Summary) is keyed to the priced trip. Changing the transfer
    // type reprices the base, so drop any prior override — it would otherwise ride onto a different trip.
    if (Number(selectedTransfer.id) !== committedTransfer?.id) dispatch(setFixedPrice(undefined));
    dispatch(setNumOfPassengers(passengers));
    dispatch(setNumOfBags(bags));
    dispatch(setBookingExtras(selectedExtras));
    if (layout === "sheet") onClose();
    else onAdvance?.();
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ViewOnlyFieldset>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-5">
        <Typography variant="h2">Vehicle and Transfer</Typography>
        <Typography variant="muted" className="mt-1 block text-neutral-dark-gray">
          Select vehicle and transfer type
        </Typography>

        {isLoading ? (
          <div className="mt-10 flex flex-1 items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <div className="mt-6 flex flex-col gap-6">
            <Field label="Select Transfer Type">
              <DropdownSelect
                // Remount on value change so the (defaultValue-seeded) Listbox never keeps a stale
                // internal selection after a programmatic change.
                key={`transfer-${transferTypeId ?? ""}`}
                className="w-full"
                size="md"
                disabled={viewOnly}
                placeholder="Select Transfer Type"
                hasError={!!errors.transferTypeId}
                options={transferTypes.map((t) => ({ name: t.label, value: String(t.id) }))}
                value={transferTypeId}
                onChange={(value: string) => {
                  setTransferTypeId(value);
                  setErrors((e) => ({ ...e, transferTypeId: undefined }));
                }}
              />
              {errors.transferTypeId && (
                <Typography variant="paragraph" className="block text-danger">
                  {errors.transferTypeId}
                </Typography>
              )}
            </Field>

            {/* Duration — only for hourly/daily transfer types; presets come from /v1/cust/data. */}
            {rateKind !== "journey" && (
              <Field label="Trip Duration">
                <DropdownSelect
                  key={`duration-${duration ?? ""}`}
                  className="w-full"
                  size="md"
                  disabled={viewOnly}
                  placeholder="Select Duration"
                  hasError={!!errors.duration}
                  options={durationOptions.map((o) => ({ name: o.label, value: String(o.value) }))}
                  value={duration != null ? String(duration) : undefined}
                  onChange={(value: string) => {
                    setDuration(Number(value));
                    setErrors((e) => ({ ...e, duration: undefined }));
                  }}
                />
                {errors.duration && (
                  <Typography variant="paragraph" className="block text-danger">
                    {errors.duration}
                  </Typography>
                )}
              </Field>
            )}

            <Field label="Select Vehicle">
              <DropdownSelect
                key={`vehicle-${vehicleId ?? ""}`}
                className="w-full"
                size="md"
                disabled={viewOnly}
                placeholder="Select Vehicle"
                hasError={!!errors.vehicleId}
                options={availableVehicles.map((v) => ({ name: v.name, value: String(v.vehicle_id) }))}
                value={vehicleId}
                onChange={(value: string) => {
                  setVehicleId(value);
                  setErrors((e) => ({ ...e, vehicleId: undefined }));
                }}
              />
              {errors.vehicleId && (
                <Typography variant="paragraph" className="block text-danger">
                  {errors.vehicleId}
                </Typography>
              )}
            </Field>

            {extras.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="flex items-baseline justify-between gap-2">
                  <Typography variant="action" className="block text-neutral-dark-gray">
                    Extras
                  </Typography>
                  <Typography variant="action" className="text-neutral-silver-gray">
                    {`${extrasCount} added`}
                  </Typography>
                </div>
                <div className="mt-0.5 border-b border-neutral-mid-gray" />

                {chosenExtras.length === 0 ? (
                  // Nothing chosen yet: the only affordance is the one that opens the picker.
                  !viewOnly && (
                    // `custom` is the atom's transparent variant — no fill, no border — which is the
                    // text-button the design asks for. Padding is dropped so it lines up with the
                    // label above it rather than sitting inset.
                    <Button variant="custom" size="sm" startIcon="Add" className="!px-0 self-start text-info" onClick={() => setExtrasOpen(true)}>
                      Add Extras
                    </Button>
                  )
                ) : (
                  <div className="flex items-center gap-3 rounded-lg border border-neutral-gray bg-neutral-surface-gray py-3 pl-4 pr-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center">
                        <Typography variant="action">{`${extrasCount} item${extrasCount === 1 ? "" : "s"}`}</Typography>
                        <span className="mx-2 h-1.5 w-1.5 rounded-full bg-neutral-mid-gray" />
                        <Typography variant="paragraph">{formatCurrency(extrasTotal, 2, "AUD")}</Typography>
                      </div>
                      {/* One line, truncated — the modal is where the full list lives. */}
                      <Typography variant="paragraph" className="mt-1 block truncate text-neutral-dark-gray">
                        {extrasLabel(chosenExtras)}
                      </Typography>
                    </div>
                    {!viewOnly && (
                      <button type="button" className="flex-shrink-0" onClick={() => setExtrasOpen(true)}>
                        <Typography variant="action" className="text-info">
                          Edit
                        </Typography>
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            <Field label="Number of Passenger">
              <div className="relative px-1">
                <RangeSlider
                  className="passenger-slider"
                  disabled={viewOnly}
                  min={1}
                  max={maxPassengers}
                  step={1}
                  value={passengers}
                  onChange={(value) => setPassengers(Array.isArray(value) ? value[0] : value)}
                  // Value bubble lives inside the handle so it overlaps (never reserves space) and
                  // shows only while dragging. The downward diamond points at the handle.
                  handleRender={(origin, info) =>
                    cloneElement(
                      origin,
                      undefined,
                      info.dragging ? (
                        <span className="pointer-events-none absolute bottom-full left-1/2 mb-2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-primary px-2.5 py-1 text-sm font-semibold text-white">
                          {info.value}
                          <span className="absolute left-1/2 top-full h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-primary" />
                        </span>
                      ) : null
                    )
                  }
                />
                {/* Ticks 8px below the track (mt-2), each with its number 2px under it (gap-0.5),
                    positioned at each value's % so they line up with the handle. Numbered stops get
                    a full-height dark tick and the stops between them a short grey one, so a dark
                    hairline always means "there is a number here"; the selected stop goes primary.
                    A skipped stop never grows its own number when selected — the drag bubble above
                    the handle already says the value. Decorative: rc-slider's handle announces it. */}
                <div ref={measureScale} className="relative mt-2 h-[26px]" aria-hidden>
                  {Array.from({ length: maxPassengers }, (_, i) => i + 1).map((n) => {
                    const labelled = labelledStops.has(n);
                    const live = n === passengers;
                    // Thinned-out stops still render if they are numbered or selected.
                    if (!labelled && !live && (n - 1) % tickStep !== 0) return null;
                    return (
                      <div
                        key={n}
                        className="absolute top-0 flex -translate-x-1/2 flex-col items-center gap-0.5"
                        style={{ left: `${maxPassengers > 1 ? ((n - 1) / (maxPassengers - 1)) * 100 : 0}%` }}
                      >
                        <span
                          className={clsx(
                            "w-px rounded-full",
                            labelled || live ? "h-2" : "h-1",
                            tickTone[live ? "live" : labelled ? "numbered" : "between"]
                          )}
                        />
                        {labelled && (
                          <Typography variant="small" className={clsx("leading-none", numberTone[live ? "live" : "fixed"])}>
                            {n}
                          </Typography>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Field>

            <Field label="Number of Bags">
              {/* Fixed 64×40 chips, 10px apart. `!` overrides the atom's baked-in sm:flex-1 stretch,
                  px-6/py-4, and gap-3 so the buttons don't grow to fill the row. */}
              <RadioButtonGroup className="flex-wrap !gap-2.5" value={bags} onChange={(value: number) => setBags(value)}>
                {Array.from({ length: maxBags + 1 }, (_, i) => i).map((n) => (
                  <RadioButtonGroup.Option key={n} name={String(n)} value={n} disabled={viewOnly} className="h-10 w-16 !flex-none !px-0 !py-0" />
                ))}
              </RadioButtonGroup>
            </Field>
          </div>
        )}
      </div>
      </ViewOnlyFieldset>

      {/* Outside ViewOnlyFieldset: that fieldset is `disabled` in view-only mode and would make every
          control inside the modal inert. It only opens from buttons that view-only never renders. */}
      <AddExtrasModal
        open={extrasOpen}
        options={extras}
        selected={selectedExtras}
        onClose={() => setExtrasOpen(false)}
        onConfirm={(chosen) => {
          setSelectedExtras(chosen);
          setExtrasOpen(false);
        }}
      />

      {!viewOnly && (
        <div className="flex flex-col gap-2 px-5 pb-5">
          <Button variant="primary" size="lg" className="w-full" onClick={handleProceed}>
            {isEdit ? "Confirm" : "Proceed"}
          </Button>
          {layout === "sheet" && (
            <Button variant="custom" size="md" className="text-info" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
